import React, {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import QUERY_KEYS from '../../constants/queryKeys';
import {handleGetGroupedRolesService} from '../../services/roles';
import {useDebounce} from '../../hooks/useDebunce';
import {GroupedRolesResponse} from '../../pages/users/components/RoleScreens/roles';
import {ApiResponse} from '../../services/axiosInstance';
import {
  BranchData,
  ClientDataType,
  SubBranchData,
  useClientsContext,
} from '../clients/ClientsContextProvider';

interface RolesContextType {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  selectedRoles: Array<string>;
  setSelectedRoles: React.Dispatch<React.SetStateAction<Array<string>>>;
  selectedRole: string | null;
  setSelectedRole: React.Dispatch<React.SetStateAction<string | null>>;
  showAddEditDialog: boolean;
  setShowAddEditDialog: React.Dispatch<React.SetStateAction<boolean>>;

  groupedRoles: GroupedRolesResponse | undefined;
  refetchGroupedRoles: () => void;
}

const RolesContext = React.createContext<RolesContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

function buildLocationParams(
  selectedLocation: ClientDataType | BranchData | SubBranchData | null,
) {
  if (!selectedLocation) return null;

  if (selectedLocation.isClient) {
    return {clientIds: [selectedLocation.id]};
  }

  if (selectedLocation.isBranch) {
    return {
      clientIds: [(selectedLocation as BranchData).clientId],
      branchIds: [selectedLocation.id],
    };
  }

  const sub = selectedLocation as SubBranchData;
  return {
    clientIds: [sub.clientId],
    branchIds: [sub.branchId],
    subBranchIds: [sub.id],
  };
}

function RolesContextProvider({children}: Props) {
  const {selectedLocation} = useClientsContext();

  const [searchText, setSearchText] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Array<string>>([]);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);

  const debouncedSearch = useDebounce(searchText, 500);

  const locationParams = buildLocationParams(selectedLocation);

  const {data: groupedRoles, refetch: refetchGroupedRoles} = useQuery<
    ApiResponse<GroupedRolesResponse>,
    Error,
    GroupedRolesResponse
  >({
    queryKey: [QUERY_KEYS.GET_GROUPED_ROLES, selectedLocation, debouncedSearch],
    queryFn: () =>
      handleGetGroupedRolesService({
        ...locationParams!,
        search: debouncedSearch,
      }),
    select: data => data?.data,
    enabled: !!selectedLocation && !!locationParams,
  });

  useEffect(() => {
    setSelectedRoles([]);
  }, [selectedLocation]);

  return (
    <RolesContext.Provider
      value={{
        searchText,
        setSearchText,
        selectedRoles,
        setSelectedRoles,
        selectedRole,
        setSelectedRole,
        showAddEditDialog,
        setShowAddEditDialog,

        groupedRoles,
        refetchGroupedRoles,
      }}>
      {children}
    </RolesContext.Provider>
  );
}

export default RolesContextProvider;

export const useRolesContext = () => {
  const context = React.useContext(RolesContext);
  if (context === null) {
    throw new Error(
      'useRolesContext must be used within an RolesContextProvider',
    );
  }
  return context;
};
